import React from 'react';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type SettingsIcon = React.ComponentType<{ className?: string }>;

interface SettingsSectionHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  icon?: SettingsIcon;
  action?: React.ReactNode;
  meta?: React.ReactNode;
  className?: string;
}

export function SettingsSectionHeader({
  eyebrow = 'Settings Console',
  title,
  description,
  icon: Icon,
  action,
  meta,
  className,
}: SettingsSectionHeaderProps) {
  return (
    <div
      className={cn(
        'settings-section-header',
        className,
      )}
    >
      <div className="settings-section-header__inner">
        <div className="settings-section-header__identity">
          {Icon && (
            <div className="settings-section-header__icon">
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div className="settings-section-header__copy">
            <div className="settings-section-header__meta">
              <span>
                {eyebrow}
              </span>
              {meta}
            </div>
            <h1>
              {title}
            </h1>
            {description && (
              <p>
                {description}
              </p>
            )}
          </div>
        </div>
        {action && <div className="settings-section-header__action">{action}</div>}
      </div>
    </div>
  );
}

interface SettingsPanelProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function SettingsPanel({
  title,
  description,
  action,
  children,
  className,
  bodyClassName,
}: SettingsPanelProps) {
  return (
    <section
      className={cn(
        'settings-panel',
        className,
      )}
    >
      {(title || description || action) && (
        <div className="settings-panel__header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action && <div className="settings-panel__action">{action}</div>}
        </div>
      )}
      <div className={cn('settings-panel__body', bodyClassName)}>{children}</div>
    </section>
  );
}

interface SettingsToolbarProps {
  children: React.ReactNode;
  className?: string;
}

export function SettingsToolbar({ children, className }: SettingsToolbarProps) {
  return (
    <div
      className={cn(
        'settings-toolbar',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface SettingsFieldGridProps {
  children: React.ReactNode;
  className?: string;
}

export function SettingsFieldGrid({ children, className }: SettingsFieldGridProps) {
  return (
    <div className={cn('settings-field-grid', className)}>
      {children}
    </div>
  );
}

interface SettingsDataViewProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function SettingsDataView({
  title,
  description,
  action,
  children,
  className,
}: SettingsDataViewProps) {
  return (
    <div className={cn('settings-data-view', className)}>
      {(title || description || action) && (
        <div className="settings-data-view__header">
          <div>
            {title && <h3>{title}</h3>}
            {description && <p>{description}</p>}
          </div>
          {action && <div className="settings-data-view__action">{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

interface SettingsDialogLayoutProps {
  icon?: SettingsIcon;
  title: string;
  description?: string;
  asideTitle?: string;
  asideDescription?: string;
  asideContent?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function SettingsDialogLayout({
  icon: Icon,
  title,
  description,
  asideTitle,
  asideDescription,
  asideContent,
  children,
  footer,
  className,
}: SettingsDialogLayoutProps) {
  return (
    <div className={cn('settings-dialog-layout', className)}>
      <aside className="settings-dialog-layout__aside">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="settings-dialog-layout__icon">
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div>
            {asideTitle && <p className="settings-dialog-layout__aside-title">{asideTitle}</p>}
            {asideDescription && (
              <p className="settings-dialog-layout__aside-description">{asideDescription}</p>
            )}
          </div>
        </div>
        {asideContent}
      </aside>

      <div className="settings-dialog-layout__main">
        <DialogHeader className="settings-dialog-layout__header">
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="settings-dialog-layout__body">{children}</div>
        {footer && (
          <DialogFooter className="settings-dialog-layout__footer">
            {footer}
          </DialogFooter>
        )}
      </div>
    </div>
  );
}
